import { useEffect, useState } from 'react';

import { AnalyticsOutlined, BarChart, Launch } from '@mui/icons-material';
import { Box, Button, Link } from '@mui/material';
import { useNavigate } from 'react-router';

import CustomCheckbox from '@/components/Checkbox';
import GoLogo from '@/components/GoLogo';
import Typography from '@/components/Typography';
import { spacing } from '@/constants/ui';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import DefaultLayout from '@/layouts/DefaultLayout';
import { acceptTerms } from '@/slices/authSlice';

const Welcome = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [accepted, setAccepted] = useState(false);

  const authState = useAppSelector(state => state.auth);
  useEffect(() => {
    if (authState.status === 'fulfilled' && authState.terms) navigate('/markets');
  }, [authState.status, authState.terms, navigate]);

  return (
    <DefaultLayout>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          mt: { xs: 0, md: '8%' },
          justifyContent: {
            xs: 'center',
            md: 'flex-start'
          },
          gap: spacing['3']
        }}
      >
        <GoLogo size={74} />
        <Box>
          <Typography
            size={32}
            sx={{ fontWeight: 700, textAlign: 'center', mb: 1 }}
          >
            Welcome to GX Go!
          </Typography>
          <Typography
            fontWeight={500}
            size={16}
            sx={{ maxWidth: 464, textAlign: 'center' }}
          >
            You're about to access robust and reliable commodity prices based on the most data,
            trusted by thousands of market leaders.
          </Typography>
        </Box>

        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #1F2634',
            background: '#0A0D21'
          }}
        >
          <Box
            display='flex'
            flexDirection='column'
          >
            <Box
              display='flex'
              flexDirection='column'
              mb={2}
            >
              <Box
                alignItems='center'
                display='flex'
                flexDirection='row'
                mb={0.5}
              >
                <BarChart sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography sx={{ ml: 1, fontWeight: 600 }}>Market Insights</Typography>
              </Box>
              <Typography sx={{ ml: 3, fontSize: 14, maxWidth: 502 }}>
                See thousands of spot prices and forward curves across global energy markets,
                exploring histories, charts and trends.
              </Typography>
            </Box>
            <Box
              display='flex'
              flexDirection='column'
              mb={2}
            >
              <Box
                alignItems='center'
                display='flex'
                flexDirection='row'
                mb={0.5}
              >
                <AnalyticsOutlined sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography sx={{ ml: 1, fontWeight: 600 }}>Transparent Methodologies</Typography>
              </Box>
              <Typography sx={{ ml: 3, fontSize: 14, maxWidth: 502 }}>
                Understand the methodology behind the index, accessing flowcharts, criteria and
                approach.
              </Typography>
            </Box>
            <Box
              display='flex'
              flexDirection='column'
            >
              <Box
                alignItems='center'
                display='flex'
                flexDirection='row'
                mb={0.5}
              >
                <Launch sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography sx={{ ml: 1, fontWeight: 600 }}>Easy Export</Typography>
              </Box>
              <Typography sx={{ ml: 3, fontSize: 14, maxWidth: 502 }}>
                Get our data where you need it, whether that's Excel, API, Python, cloud or market
                data terminals.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CustomCheckbox
            checked={accepted}
            onChange={() => {
              setAccepted(prev => !prev);
            }}
          />
          <Typography sx={{ ml: 1, fontSize: 14 }}>
            I confirm that I have read and accept the{' '}
            <Link
              href='/terms.pdf'
              rel='noopener noreferrer'
              target='_blank'
              underline='hover'
            >
              Terms and Conditions
            </Link>{' '}
            and{' '}
            <Link
              href='https://www.general-index.com/privacy-cookies'
              rel='noopener noreferrer'
              target='_blank'
              underline='hover'
            >
              Privacy Policy
            </Link>
            .
          </Typography>
        </Box>

        <Button
          fullWidth
          disabled={!accepted}
          sx={{
            fontWeight: 500,
            fontSize: 16,
            width: 300
          }}
          variant='contained'
          onClick={() => {
            dispatch(acceptTerms(new Date(Date.now()).toISOString()));
            navigate(-1);
          }}
        >
          Access GX Go
        </Button>
      </Box>
    </DefaultLayout>
  );
};

export default Welcome;
